<template>
  <div class="ai-chat-messages" ref="container" role="log" aria-live="polite">
    <div v-if="messages.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fa-solid fa-robot"></i>
      </div>
      <h4>{{ agentName }}</h4>
      <p>
        Olá! Sou o assistente IA deste curso.<br />
        Pergunte-me qualquer coisa sobre o conteúdo.
      </p>
      <div class="suggestions">
        <button
          v-for="(suggestion, i) in suggestions"
          :key="i"
          class="btn-suggestion"
          @click="$emit('send', suggestion)"
        >
          {{ suggestion }}
        </button>
      </div>
    </div>

    <template v-else>
      <AIChatBubble
        v-for="(message, index) in messages"
        :key="index"
        :message="message"
      />
    </template>

    <div
      v-if="isStreaming && lastMessageEmpty"
      class="typing-indicator"
    >
      <span></span>
      <span></span>
      <span></span>
    </div>

    <div ref="anchor" class="scroll-anchor"></div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import AIChatBubble from "./AIChatBubble.vue";

export default {
  name: "AIChatMessages",
  components: { AIChatBubble },
  data() {
    return {
      scrollThrottle: null
    };
  },
  computed: {
    ...mapState("agent", ["messages", "isStreaming"]),
    ...mapGetters("agent", ["agentName"]),
    lastMessageEmpty() {
      if (!this.messages.length) return true;
      const last = this.messages[this.messages.length - 1];
      return last.role === "assistant" && !last.content;
    },
    suggestions() {
      return [
        "Resuma o conteúdo deste curso",
        "Quais são os principais tópicos?",
        "Me ajude a entender melhor"
      ];
    }
  },
  watch: {
    messages: {
      deep: true,
      handler() {
        if (this.scrollThrottle) return;
        this.scrollThrottle = setTimeout(() => {
          this.scrollThrottle = null;
          this.$nextTick(() => this.scrollToBottom());
        }, 100);
      }
    }
  },
  beforeDestroy() {
    if (this.scrollThrottle) {
      clearTimeout(this.scrollThrottle);
    }
  },
  methods: {
    scrollToBottom() {
      const anchor = this.$refs.anchor;
      if (anchor) {
        anchor.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

.scroll-anchor {
  height: 1px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 24px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(171, 133, 229, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  i {
    font-size: 24px;
    color: #ab85e5;
  }
}

.empty-state h4 {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;
}

.empty-state p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.6;
  margin-bottom: 20px;
}

.suggestions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 280px;
}

.btn-suggestion {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    background: rgba(171, 133, 229, 0.08);
    border-color: rgba(171, 133, 229, 0.25);
    color: rgba(255, 255, 255, 0.85);
  }
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 16px;
  margin-left: 42px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ab85e5;
    opacity: 0.4;
    animation: typing 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing {
  0%,
  60%,
  100% {
    opacity: 0.2;
    transform: scale(0.8);
  }
  30% {
    opacity: 0.8;
    transform: scale(1);
  }
}
</style>
